import { Button, Dialog, IconButton } from "@au-schein-de/au-ui-library";
import { XIcon } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { TransformedImage } from "~/components/transformed-image.component";
import { COURIER_EXPRESS_AVAILABILITY_ILLUSTRATION_SRC } from "~/constants/courier-delivery-illustration";

interface ExpressAvailabilityModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onShowCourierPharmacies: () => void;
}

/**
 * Discovery modal shown on the product list when express courier delivery is
 * available for the customer's area. Presentational only — the trigger, the
 * rollout flag and the courier pharmacy navigation are owned by the parent.
 */
export function ExpressAvailabilityModal({
	open,
	onOpenChange,
	onShowCourierPharmacies,
}: Readonly<ExpressAvailabilityModalProps>) {
	const { t } = useTranslation("product");
	const dismiss = () => onOpenChange(false);
	const handleShowCourierPharmacies = () => {
		onShowCourierPharmacies();
		dismiss();
	};

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Content
				withCloseButton={false}
				className="!block !gap-0 !p-0 md:w-[570px]"
			>
				<div
					data-testid="express-availability-modal"
					className="flex w-full flex-col items-center rounded-xl bg-white pb-8 md:pb-12"
				>
					<div className="flex w-full items-center justify-end p-4">
						<IconButton
							shape="circle"
							color="gray"
							icon={<XIcon size={20} />}
							onClick={dismiss}
							aria-label={t("expressAvailability.modal.close")}
							data-testid="express-availability-modal-close"
							className="!text-gray-700"
						/>
					</div>

					<TransformedImage
						src={COURIER_EXPRESS_AVAILABILITY_ILLUSTRATION_SRC}
						alt=""
						width={127}
						height={138}
						loading="lazy"
						className="h-[113px] w-[104px] shrink-0 object-contain md:h-[138px] md:w-[127px]"
					/>

					<div className="flex w-full flex-col items-center gap-6 px-4 pt-3 md:px-10 md:pt-6">
						<div className="flex w-full flex-col gap-2">
							<Dialog.Title
								className="text-h4 text-gray-700 md:text-h3"
								data-testid="express-availability-modal-title"
							>
								{t("expressAvailability.modal.titleLead")}{" "}
								<span className="text-ansay-main">
									{t("expressAvailability.modal.titleHighlight")}
								</span>
							</Dialog.Title>
							<Dialog.Description asChild>
								<p className="text-body text-gray-500">
									<Trans
										i18nKey="expressAvailability.modal.description"
										ns="product"
										components={{
											bold: <span className="text-body-bold" />,
										}}
									/>
								</p>
							</Dialog.Description>
						</div>

						<div className="flex w-full flex-col items-center gap-3 md:gap-6">
							<Button
								type="button"
								variant="raw"
								className="text-body-emphasis h-12 w-full cursor-pointer rounded-xl bg-ansay-light text-gray-700 md:h-14"
								data-testid="express-availability-modal-courier-pharmacies"
								onClick={handleShowCourierPharmacies}
							>
								{t("expressAvailability.modal.showCourierPharmacies")}
							</Button>
							<button
								type="button"
								className="text-body-bold h-14 cursor-pointer py-3 text-ansay-700"
								data-testid="express-availability-modal-shipping-pharmacies"
								onClick={dismiss}
							>
								{t("expressAvailability.modal.showShippingPharmacies")}
							</button>
						</div>
					</div>
				</div>
			</Dialog.Content>
		</Dialog.Root>
	);
}
